/**
 * Take an evaluation copy out whole: its request, its report, everything
 * hanging off either, and its files. The case it was made from is untouched.
 */
import { useState } from 'react';

import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { toError } from '@/utils/errors';

interface RemoveCopyButtonProps {
  reportId: string;
  onDone: () => void | Promise<void>;
}

export function RemoveCopyButton({ reportId, onDone }: Readonly<RemoveCopyButtonProps>) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await vnextApiClient.removeWorkbenchCopy(reportId);
      notifications.show({
        color: 'teal',
        message: `Removed — ${result.filesDeleted} file${result.filesDeleted === 1 ? '' : 's'} deleted with it.`,
      });
      setOpen(false);
      await onDone();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: toError(err, 'Could not remove').message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="xs"
        variant="default"
        leftSection={<IconTrash size={14} />}
        onClick={() => setOpen(true)}
        data-testid="remove-copy"
      >
        Remove this copy
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title="Remove this evaluation copy?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            The copy goes whole: its request, its report, the work on it and its files. The case it
            was made from is untouched. A run under way holds this until it ends.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              color="red"
              loading={busy}
              onClick={() => void confirm()}
              data-testid="confirm-remove-copy"
            >
              Remove this copy
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
